import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Plane, Clock, Navigation } from "lucide-react";

const hash = (s) => [...s].reduce((a, c) => a + c.charCodeAt(0), 0);

export default function Simulator() {
  const [origin, setOrigin] = useState("Home, Brooklyn, NY");
  const [airport, setAirport] = useState("JFK · Terminal 4");
  const [flight, setFlight] = useState("DL 412");
  const [result, setResult] = useState(null);

  const compute = () => {
    const h = hash(origin + airport + flight);
    const traffic = 18 + (h % 26);
    const tsa = 8 + (h % 18);
    const walk = 5 + (h % 9);
    const buffer = 20;
    const leaveIn = traffic + tsa + walk + buffer;
    const leaveAt = new Date(Date.now() + leaveIn * 60000);
    setResult({
      traffic,
      tsa,
      walk,
      buffer,
      leaveIn,
      leaveAt: leaveAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    });
  };

  const total = result ? result.traffic + result.tsa + result.walk + result.buffer : 1;

  return (
    <div data-testid="hero-simulator" className="glass-panel glow-border rounded-2xl p-6 sm:p-7 noise-overlay relative">
      <div className="flex items-center justify-between mb-6">
        <span className="font-mono text-[11px] tracking-widest text-primary uppercase">
          Door-to-Gate Simulator
        </span>
      </div>

      <div className="space-y-3">
        <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 focus-within:border-primary/50 transition-colors">
          <MapPin size={16} className="text-primary shrink-0" />
          <input
            data-testid="hero-simulator-input-origin"
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            placeholder="Starting point"
            className="w-full bg-transparent text-sm text-slate-100 placeholder:text-slate-500 outline-none"
          />
        </label>
        <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 focus-within:border-primary/50 transition-colors">
          <Navigation size={16} className="text-[#FFB100] shrink-0" />
          <input
            data-testid="hero-simulator-input-destination"
            value={airport}
            onChange={(e) => setAirport(e.target.value)}
            placeholder="Airport & terminal"
            className="w-full bg-transparent text-sm text-slate-100 placeholder:text-slate-500 outline-none"
          />
        </label>
        <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 focus-within:border-primary/50 transition-colors">
          <Plane size={16} className="text-slate-400 shrink-0" />
          <input
            data-testid="hero-simulator-input-flight"
            value={flight}
            onChange={(e) => setFlight(e.target.value)}
            placeholder="Flight number"
            className="w-full bg-transparent text-sm text-slate-100 placeholder:text-slate-500 outline-none"
          />
        </label>
      </div>

      <button
        data-testid="hero-simulator-calculate-button"
        onClick={compute}
        className="mt-4 w-full rounded-xl bg-primary py-3.5 text-sm font-bold tracking-wide text-[#0B0B0D] transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
      >
        COMPUTE MY LEAVE TIME
      </button>

      <AnimatePresence mode="wait">
        {result && (
          <motion.div
            key={result.leaveAt + result.leaveIn}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 rounded-xl border border-primary/25 bg-primary/[0.06] p-5"
            data-testid="hero-simulator-result"
          >
            <div className="flex items-end justify-between">
              <div>
                <p className="font-mono text-[11px] tracking-widest text-slate-400 uppercase">Leave in</p>
                <p className="font-mono text-3xl font-bold text-white mt-1">
                  {result.leaveIn}
                  <span className="text-sm text-slate-400 ml-1.5">MIN</span>
                </p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[11px] tracking-widest text-slate-400 uppercase">Out the door at</p>
                <p className="font-mono text-2xl font-bold text-primary text-glow mt-1">{result.leaveAt}</p>
              </div>
            </div>

            <div className="mt-5 flex h-2.5 w-full overflow-hidden rounded-full bg-white/5">
              {[
                { v: result.traffic, c: "#1E90FF" },
                { v: result.tsa, c: "#FFB100" },
                { v: result.walk, c: "#00BFA5" },
                { v: result.buffer, c: "#64748B" },
              ].map((seg, i) => (
                <motion.div
                  key={i}
                  initial={{ width: 0 }}
                  animate={{ width: `${(seg.v / total) * 100}%` }}
                  transition={{ duration: 0.8, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                  style={{ background: seg.c }}
                />
              ))}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 font-mono text-[11px] tracking-wider">
              <span className="flex items-center gap-2 text-slate-400">
                <span className="w-2 h-2 rounded-sm" style={{ background: "#1E90FF" }} />
                TRAFFIC {result.traffic} MIN
              </span>
              <span className="flex items-center gap-2 text-slate-400">
                <span className="w-2 h-2 rounded-sm" style={{ background: "#FFB100" }} />
                TSA {result.tsa} MIN
              </span>
              <span className="flex items-center gap-2 text-slate-400">
                <span className="w-2 h-2 rounded-sm" style={{ background: "#00BFA5" }} />
                GATE WALK {result.walk} MIN
              </span>
              <span className="flex items-center gap-2 text-slate-400">
                <span className="w-2 h-2 rounded-sm" style={{ background: "#64748B" }} />
                BUFFER {result.buffer} MIN
              </span>
            </div>

            <p className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
              <Clock size={12} /> Simulated preview. The app computes from live data feeds.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
